import React, { useState } from 'react';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiAward,
  FiCheckCircle,
  FiMail,
  FiMapPin,
  FiPhone,
  FiUser,
} from 'react-icons/fi';

import Logo from '../components/common/Logo';
import TextField from '../components/auth/TextField';
import { submitApplication } from '../api/client';
import { FALLBACK_CATEGORIES } from '../api/fallback';

const MODALITIES = [
  { key: 'video', label: 'Video sessions' },
  { key: 'in-person', label: 'In person' },
  { key: 'phone', label: 'Telephone' },
];

const MIN_BIO = 80;

const EMPTY = {
  fullName: '',
  email: '',
  phone: '',
  title: '',
  registrationBody: '',
  registrationNumber: '',
  location: '',
  website: '',
  bio: '',
  fee: '',
  yearsExperience: '',
  specialties: [],
  modalities: [],
  acceptedTerms: false,
  confirmedRegistration: false,
};

/** Mirrors backend/applications.py. The server revalidates everything. */
function validate(v) {
  const e = {};
  if (!v.fullName.trim()) e.fullName = 'Please enter your full name.';
  if (!v.email.trim()) e.email = 'Please enter your email address.';
  else if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(v.email.trim()))
    e.email = 'That doesn’t look like a valid email address.';
  if (!v.title.trim()) e.title = 'Please enter your professional title.';
  if (!v.registrationBody.trim())
    e.registrationBody = 'Tell us which body you’re registered with.';
  if (!v.registrationNumber.trim())
    e.registrationNumber = 'Please enter your registration number.';
  if (!v.location.trim()) e.location = 'Where do you practise?';
  if (!v.bio.trim()) e.bio = 'Please write a short introduction.';
  else if (v.bio.trim().length < MIN_BIO)
    e.bio = `Please write at least ${MIN_BIO} characters — clients read this.`;
  if (v.fee === '' || Number.isNaN(Number(v.fee)))
    e.fee = 'Please enter your session fee.';
  else if (Number(v.fee) < 0) e.fee = 'A fee can’t be negative.';
  if (v.yearsExperience === '' || Number.isNaN(Number(v.yearsExperience)))
    e.yearsExperience = 'How many years have you been practising?';
  if (!v.specialties.length) e.specialties = 'Choose at least one specialty.';
  if (!v.modalities.length) e.modalities = 'Choose at least one way you see clients.';
  if (!v.acceptedTerms) e.acceptedTerms = 'Please confirm the declaration to continue.';
  if (!v.confirmedRegistration)
    e.confirmedRegistration = 'Please confirm your registration is current.';
  return e;
}

function Success({ reference }) {
  return (
    <div className="mm-apply__done">
      <span className="mm-apply__done-icon" aria-hidden="true">
        <FiCheckCircle size={30} />
      </span>
      <h1 className="mm-h2">Application received</h1>
      <p className="mm-lead">
        Your reference is <strong>{reference}</strong>. Our clinical team
        verifies registrations within 5 working days, and we&rsquo;ll email you
        either way.
      </p>
      <p className="mm-faint" style={{ fontSize: '0.9rem' }}>
        Nothing is listed publicly until a human has checked your credentials.
      </p>
      <Link to="/" className="btn btn-primary px-4 mt-2">
        Back to site
      </Link>
    </div>
  );
}

export default function ApplyPage() {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);

  const set = (patch) => {
    setValues((prev) => ({ ...prev, ...patch }));
    setFormError(null);
  };

  const handleChange = (event) => {
    const { name, type, value, checked } = event.target;
    set({ [name]: type === 'checkbox' ? checked : value });
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const toggleIn = (key, item) => {
    const current = values[key];
    set({
      [key]: current.includes(item)
        ? current.filter((v) => v !== item)
        : [...current, item],
    });
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);

    const clientErrors = validate(values);
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      // Move focus to the first problem rather than leaving them to hunt.
      document
        .querySelector('[aria-invalid="true"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitApplication({
        ...values,
        fee: Number(values.fee),
        yearsExperience: Number(values.yearsExperience),
      });
      setReference(result.reference);
    } catch (err) {
      if (err.body?.errors) {
        setErrors(err.body.errors);
      } else {
        setFormError(
          err.status
            ? err.message
            : 'Couldn’t reach the server. Check your connection and try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mm-apply">
      <header className="mm-admin__bar">
        <Container className="mm-admin__bar-inner">
          <Link to="/" className="mm-admin__brand" aria-label="MindlyFriend home">
            <Logo markSize={32} />
          </Link>
          <Link to="/" className="mm-auth__back">
            <FiArrowLeft size={15} aria-hidden="true" />
            Back to site
          </Link>
        </Container>
      </header>

      <Container className="mm-apply__body">
        {reference ? (
          <Success reference={reference} />
        ) : (
          <Row className="g-4 g-lg-5">
            <Col lg={4}>
              <div className="mm-apply__intro">
                <span className="mm-eyebrow">For clinicians</span>
                <h1 className="mm-h2 mt-2">Apply to join MindlyFriend</h1>
                <p className="mm-lead mt-3">
                  We verify every clinician before they appear in the directory.
                  This takes about ten minutes and we&rsquo;ll come back to you
                  within five working days.
                </p>

                <ul className="mm-apply__points">
                  <li>No lead fees and no commission on sessions</li>
                  <li>You set and publish your own rates</li>
                  <li>Your registration is re-verified annually</li>
                </ul>

                <p className="mm-faint mt-4" style={{ fontSize: '0.86rem' }}>
                  Already applied? We&rsquo;ll email you at the address you gave
                  us. Already listed?{' '}
                  <Link to="/clinician/login">Log in to the portal</Link>.
                </p>
              </div>
            </Col>

            <Col lg={8}>
              <form className="mm-apply__form" onSubmit={handleSubmit} noValidate>
                {formError && (
                  <div className="mm-form-alert mm-form-alert--error" role="alert">
                    <FiAlertCircle size={16} aria-hidden="true" />
                    <span>{formError}</span>
                  </div>
                )}

                {/* -- About you -------------------------------------- */}
                <fieldset className="mm-apply__section">
                  <legend>About you</legend>
                  <Row className="g-3">
                    <Col md={6}>
                      <TextField
                        label="Full name"
                        name="fullName"
                        icon={FiUser}
                        placeholder="Dr. Nisha Verma"
                        autoComplete="name"
                        required
                        value={values.fullName}
                        onChange={handleChange}
                        error={errors.fullName}
                      />
                    </Col>
                    <Col md={6}>
                      <TextField
                        label="Email address"
                        name="email"
                        type="email"
                        icon={FiMail}
                        placeholder="you@yourpractice.com"
                        autoComplete="email"
                        required
                        value={values.email}
                        onChange={handleChange}
                        error={errors.email}
                      />
                    </Col>
                    <Col md={6}>
                      <TextField
                        label="Phone"
                        name="phone"
                        icon={FiPhone}
                        placeholder="+91 98765 43210"
                        autoComplete="tel"
                        value={values.phone}
                        onChange={handleChange}
                        error={errors.phone}
                      />
                    </Col>
                    <Col md={6}>
                      <TextField
                        label="Where you practise"
                        name="location"
                        icon={FiMapPin}
                        placeholder="Jaipur, Rajasthan"
                        required
                        value={values.location}
                        onChange={handleChange}
                        error={errors.location}
                      />
                    </Col>
                  </Row>
                </fieldset>

                {/* -- Credentials ------------------------------------ */}
                <fieldset className="mm-apply__section">
                  <legend>Credentials</legend>
                  <Row className="g-3">
                    <Col md={6}>
                      <TextField
                        label="Professional title"
                        name="title"
                        icon={FiAward}
                        placeholder="Clinical Psychologist"
                        required
                        value={values.title}
                        onChange={handleChange}
                        error={errors.title}
                      />
                    </Col>
                    <Col md={6}>
                      <TextField
                        label="Years practising"
                        name="yearsExperience"
                        type="number"
                        icon={FiAward}
                        placeholder="11"
                        required
                        value={values.yearsExperience}
                        onChange={handleChange}
                        error={errors.yearsExperience}
                      />
                    </Col>
                    <Col md={6}>
                      <TextField
                        label="Registration body"
                        name="registrationBody"
                        icon={FiAward}
                        placeholder="RCI, BACP, HCPC…"
                        required
                        value={values.registrationBody}
                        onChange={handleChange}
                        error={errors.registrationBody}
                      />
                    </Col>
                    <Col md={6}>
                      <TextField
                        label="Registration number"
                        name="registrationNumber"
                        icon={FiAward}
                        placeholder="A-99881"
                        required
                        value={values.registrationNumber}
                        onChange={handleChange}
                        error={errors.registrationNumber}
                      />
                    </Col>
                  </Row>
                </fieldset>

                {/* -- Practice --------------------------------------- */}
                <fieldset className="mm-apply__section">
                  <legend>Your practice</legend>

                  <div className="mm-field">
                    <span className="mm-field__label" id="label-specialties">
                      Specialties <span className="mm-faint">(choose up to 8)</span>
                    </span>
                    {/* A group can't be aria-invalid — it isn't an input. The
                        error is announced by pointing aria-describedby at it. */}
                    <div
                      className="mm-chips"
                      role="group"
                      aria-labelledby="label-specialties"
                      aria-describedby={
                        errors.specialties ? 'error-specialties' : undefined
                      }
                    >
                      {FALLBACK_CATEGORIES.map((c) => {
                        const on = values.specialties.includes(c.name);
                        return (
                          <button
                            type="button"
                            key={c.id}
                            className={`mm-chip ${on ? 'mm-chip--on' : ''}`}
                            aria-pressed={on}
                            disabled={!on && values.specialties.length >= 8}
                            onClick={() => toggleIn('specialties', c.name)}
                          >
                            {c.name}
                          </button>
                        );
                      })}
                    </div>
                    {errors.specialties && (
                      <span
                        className="mm-field__error"
                        id="error-specialties"
                        role="alert"
                      >
                        <FiAlertCircle size={13} aria-hidden="true" />
                        {errors.specialties}
                      </span>
                    )}
                  </div>

                  <div className="mm-field">
                    <span className="mm-field__label" id="label-modalities">
                      How you see clients
                    </span>
                    <div
                      className="mm-chips"
                      role="group"
                      aria-labelledby="label-modalities"
                      aria-describedby={
                        errors.modalities ? 'error-modalities' : undefined
                      }
                    >
                      {MODALITIES.map((m) => {
                        const on = values.modalities.includes(m.key);
                        return (
                          <button
                            type="button"
                            key={m.key}
                            className={`mm-chip ${on ? 'mm-chip--on' : ''}`}
                            aria-pressed={on}
                            onClick={() => toggleIn('modalities', m.key)}
                          >
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                    {errors.modalities && (
                      <span
                        className="mm-field__error"
                        id="error-modalities"
                        role="alert"
                      >
                        <FiAlertCircle size={13} aria-hidden="true" />
                        {errors.modalities}
                      </span>
                    )}
                  </div>

                  <Row className="g-3">
                    <Col md={6}>
                      <TextField
                        label="Fee per session (₹)"
                        name="fee"
                        type="number"
                        placeholder="5500"
                        required
                        value={values.fee}
                        onChange={handleChange}
                        error={errors.fee}
                        hint="You can change this at any time once listed."
                      />
                    </Col>
                    <Col md={6}>
                      <TextField
                        label="Website"
                        name="website"
                        placeholder="https://yourpractice.com"
                        value={values.website}
                        onChange={handleChange}
                        error={errors.website}
                      />
                    </Col>
                  </Row>

                  <div className="mm-field">
                    <label className="mm-field__label" htmlFor="apply-bio">
                      Introduce yourself
                    </label>
                    <textarea
                      id="apply-bio"
                      name="bio"
                      rows={6}
                      className={`mm-field__control mm-apply__textarea ${
                        errors.bio ? 'mm-field__control--invalid' : ''
                      }`}
                      placeholder="How you work, who you work with, and what a first session looks like."
                      value={values.bio}
                      onChange={handleChange}
                      aria-invalid={Boolean(errors.bio)}
                    />
                    <span className="mm-field__hint">
                      {values.bio.trim().length} / {MIN_BIO} characters minimum
                    </span>
                    {errors.bio && (
                      <span className="mm-field__error" role="alert">
                        <FiAlertCircle size={13} aria-hidden="true" />
                        {errors.bio}
                      </span>
                    )}
                  </div>
                </fieldset>

                {/* -- Declaration ------------------------------------ */}
                <fieldset className="mm-apply__section">
                  <legend>Declaration</legend>

                  <label className="mm-check mb-3">
                    <input
                      type="checkbox"
                      name="confirmedRegistration"
                      checked={values.confirmedRegistration}
                      onChange={handleChange}
                      aria-invalid={Boolean(errors.confirmedRegistration)}
                    />
                    <span>
                      I confirm my professional registration is current, and that
                      I hold valid indemnity insurance.
                    </span>
                  </label>
                  {errors.confirmedRegistration && (
                    <span className="mm-field__error mb-3 d-block" role="alert">
                      <FiAlertCircle size={13} aria-hidden="true" />
                      {errors.confirmedRegistration}
                    </span>
                  )}

                  <label className="mm-check">
                    <input
                      type="checkbox"
                      name="acceptedTerms"
                      checked={values.acceptedTerms}
                      onChange={handleChange}
                      aria-invalid={Boolean(errors.acceptedTerms)}
                    />
                    <span>
                      I agree to the <a href="#terms">Terms of Use</a> and
                      understand MindlyFriend will verify my credentials before
                      listing me.
                    </span>
                  </label>
                  {errors.acceptedTerms && (
                    <span className="mm-field__error d-block" role="alert">
                      <FiAlertCircle size={13} aria-hidden="true" />
                      {errors.acceptedTerms}
                    </span>
                  )}
                </fieldset>

                <Button type="submit" className="px-4" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                        aria-hidden="true"
                      />
                      Submitting…
                    </>
                  ) : (
                    'Submit application'
                  )}
                </Button>
              </form>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
}
